// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useBunzlDispatch } from "@store/Framework/Store/Bunzl/BunzlReducer";
import { StepformInput } from "@store/Framework/Store/Bunzl/Pages/StepForm/StepFormReducer";
import {
    StepFormValidationDictionary,
    useCreditFormContext,
} from "@store/Library/Widgets/CreditForm/CreditFormContextContainer";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";

const tradeReferenceKeys = [
    "referenceName",
    "referenceAddress",
    "referenceCity",
    "referenceProvince",
    "referencePostalCode",
    "referencePhoneNumber",
    "referenceFaxNumber",
    "referenceEmail",
    "referenceContactName",
] as const;

export type TradeReference = Record<typeof tradeReferenceKeys[number], StepformInput>;
const tradeReferenceToStepFormValidationDictionaryKey: Record<
    keyof TradeReference,
    keyof StepFormValidationDictionary
> = {
    referenceName: "name",
    referencePhoneNumber: "phoneNumber",
    referenceCity: "city",
    referenceProvince: "province",
    referencePostalCode: "postalCode",
    referenceFaxNumber: "faxNumber",
    referenceAddress: "companyAddress",
    referenceEmail: "email",
    referenceContactName: "name",
};
const emptyTradeReference: TradeReference = tradeReferenceKeys.reduce((acc, key) => {
    if (key === "referenceFaxNumber") {
        acc[key] = { value: "", isValid: false, required: false };
        return acc;
    }
    acc[key] = { value: "", isValid: false, required: true };
    return acc;
}, {} as TradeReference);

const getNewTradeReference = (): TradeReference => {
    return { ...emptyTradeReference };
};
const useCreditFormTradeReferences = (referenceStep: number, minimumNumberOfReferences: number) => {
    const { storeKey, currentStep } = useCreditFormContext();
    const validationDictionary = useCreditFormContext().validationDictionary;
    const stepIndex = referenceStep ?? currentStep;
    const tradeReferenceStatus = useAppSelector(state => {
        const step =
            toToolkitApplicationState(state).bunzl.pages.stepForm?.[storeKey]?.steps[referenceStep ?? currentStep];
        return { tradeReferenceStepIsValid: step.valid, percentageComplete: step.percentComplete };
    });
    const bunzlDispatch = useBunzlDispatch();
    const unCheckedTradeReferences = useAppSelector(
        state =>
            toToolkitApplicationState(state).bunzl.pages.stepForm?.[storeKey]?.steps[referenceStep || currentStep]
                ?.tradeReferences,
    );
    const { percentageComplete } = tradeReferenceStatus;
    const tradeReferences = isTradeReferences(unCheckedTradeReferences)
        ? unCheckedTradeReferences
        : Array.from({ length: minimumNumberOfReferences }, () => getNewTradeReference());
    const setTradeReferences = (tradeReferences: TradeReference[]) => {
        bunzlDispatch({
            type: "Data/StepForm/UpdateCustomInputType",
            formKey: storeKey,
            inputKey: "tradeReferences",
            value: tradeReferences,
            stepIndex: referenceStep ?? currentStep,
        });
    };

    const handleTradeReferenceDelete = (tradeReferenceToDelete: TradeReference) => {
        const updatedTradeReferences = tradeReferences.filter(
            tradeReference => tradeReference !== tradeReferenceToDelete,
        );
        setTradeReferences(updatedTradeReferences);
    };

    let incorrectInputs = 0;
    let totalInputs = 0;

    tradeReferences.forEach(tradeReference =>
        Object.entries(tradeReference).forEach(([key, input]) => {
            if (key === "referenceFaxNumber") {
                return;
            }
            totalInputs += 1;
            if (!input.isValid) {
                incorrectInputs += 1;
            }
        }),
    );
    const tradeReferencesAreValid = incorrectInputs === 0;
    const percentComplete = tradeReferencesAreValid ? 100 : ((totalInputs - incorrectInputs) / totalInputs) * 100;
    const tradeReferenceHasChanged = percentageComplete !== percentComplete;

    if (tradeReferenceHasChanged) {
        bunzlDispatch({
            type: "Data/StepForm/SetStepFormIsValid",
            formKey: storeKey,
            stepIndex,
            isValid: tradeReferencesAreValid,
            percentComplete,
        });
    }
    const handleTradeReferenceChange = (
        tradeReferenceIndex: number,
        key: keyof TradeReference,
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
        const value = event.target?.value || event.currentTarget?.value;
        const validationFunction = validationDictionary[tradeReferenceToStepFormValidationDictionaryKey[key]];
        const isValid = !validationFunction(value);
        const updatedTradeReferences: TradeReference[] = tradeReferences.map((tradeReference, index) => {
            if (index === tradeReferenceIndex) {
                return {
                    ...tradeReference,
                    [key]: { value: event.target?.value || event.currentTarget?.value, isValid },
                };
            }
            return tradeReference;
        });
        setTradeReferences(updatedTradeReferences);
    };
    const handleAddTradeReference = () => {
        setTradeReferences([...tradeReferences, getNewTradeReference()]);
    };
    return { tradeReferences, handleTradeReferenceDelete, handleTradeReferenceChange, handleAddTradeReference };
};

const isTradeReferences = (tradeReferences: unknown): tradeReferences is TradeReference[] => {
    return (
        Array.isArray(tradeReferences) &&
        tradeReferences.every((tradeReference: unknown[]) => {
            return typeof tradeReference === "object" && tradeReferenceKeys.every(key => key in tradeReference);
        })
    );
};

export default useCreditFormTradeReferences;
